import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { DateTime } from 'luxon';
import { ModuleAttachmentsComponent } from 'app/shared/components/module-attachments/module-attachments.component';
import { UserDocumentSignaturesComponent } from 'app/shared/components/user-document-signatures/user-document-signatures.component';
import {
    UserSalaryHistoryFormComponent,
    UserSalaryHistoryFormDialogData,
} from './user-salary-history-form/user-salary-history-form.component';
import {
    UserHourBankFormComponent,
    UserHourBankFormDialogData,
} from './user-hour-bank-form/user-hour-bank-form.component';
import {
    UserWarningFormComponent,
    UserWarningFormDialogData,
} from './user-warning-form/user-warning-form.component';
import { UserSalaryHistoryModel } from 'app/models/user_salary_history.model';
import { UserHourBankModel } from 'app/models/user_hour_bank.model';
import { UserWarningModel } from 'app/models/user_warning.model';
import { UserSalaryHistoryService } from 'app/services/user_salary_history.service';
import { UserHourBankService } from 'app/services/user_hour_bank.service';
import { UserWarningService } from 'app/services/user_warning.service';
import { FileModuleService } from 'app/services/file_module.service';
import { FileModuleModel } from 'app/models/file_module.model';
import { FinanceService } from 'app/services/finance.service';
import { FinanceModel } from 'app/models/finance.model';
import { FinanceFormComponent } from 'app/modules/finance/form/finance-form.component';
import { financialStatusRecord } from 'app/shared/records/financial-status.record';
import { ImageViewerService } from 'app/shared/services/image-viewer.service';
import { firstValueFrom } from 'rxjs';

import { UserDocument, UserModel } from 'app/models/user.model';
import { UserService } from 'app/services/user.service';

import { PermissionModel } from 'app/models/permission.model';
import { PermissionService } from 'app/services/permission.service';

import { CompanyModel } from 'app/models/company.model';
import { CompanyService } from 'app/services/company.service';

import { ICepResponse, ViaCepService } from 'app/services/via-cep.service';
import { ApiService } from 'app/shared/apis/api.service';
import { ConfirmDialogComponent, ConfirmDialogData } from 'app/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { EDocumentType } from 'app/shared/enums/document-type.enum';
import { EMaritalStatus } from 'app/shared/enums/marital-status.enum';
import {
    EUserDocumentType,
    USER_IDENTITY_DOCUMENT_TYPES,
    USER_LABOR_DOCUMENT_TYPES,
} from 'app/shared/enums/user-document-type.enum';
import { EUserType } from 'app/shared/enums/user-type.enum';
import { EDocumentTypeOptions } from 'app/shared/options/document-type.options';
import { MaritalStatusOptions } from 'app/shared/options/marital-status.options';
import {
    UserIdentityDocumentTypeOptions,
    UserLaborDocumentTypeOptions,
} from 'app/shared/options/user-document-type.options';
import { BrazilianBankOptions } from 'app/shared/options/brazilian-banks.options';
import { BANK_ACCOUNT_TYPE_OPTIONS } from 'app/shared/utils/user-profile-form.helpers';
import { S3Service } from 'app/shared/services/s3.service';
import { ToastService } from 'app/shared/services/toast.service';
import { DocumentUtils } from 'app/shared/utils/document.utils';
import { CustomValidator } from 'app/shared/validators/custom.validator';
import { finalize, map, take, tap } from 'rxjs';

type DocRowForm = FormGroup<{
    doc_type: FormControl<EUserDocumentType | string | null>;
    doc_number: FormControl<string | null>;
    observation: FormControl<string | null>;
}>;

type UserForm = FormGroup<{
    user_id: FormControl<number | null>;
    name: FormControl<string | null>;
    phone: FormControl<string | null>;
    phone_secondary: FormControl<string | null>;
    photo_url: FormControl<string | null>;
    birth_date: FormControl<DateTime | Date | string | null>;
    marital_status: FormControl<EMaritalStatus | string | null>;
    email: FormControl<string | null>;
    email_secondary: FormControl<string | null>;
    password: FormControl<string | null>;
    sector: FormControl<string | null>;
    manager_user_id: FormControl<number | null>;
    admission_date: FormControl<DateTime | Date | string | null>;
    termination_date: FormControl<DateTime | Date | string | null>;
    current_role_title: FormControl<string | null>;
    street: FormControl<string | null>;
    number: FormControl<string | null>;
    complement: FormControl<string | null>;
    neighboorhod: FormControl<string | null>;
    city: FormControl<string | null>;
    uf: FormControl<string | null>;
    observation: FormControl<string | null>;
    cep: FormControl<string | null>;
    type_doc: FormControl<EDocumentType | null>;
    doc_number: FormControl<string | null>;
    bank_name: FormControl<string | null>;
    bank_agency: FormControl<string | null>;
    bank_account: FormControl<string | null>;
    bank_account_type: FormControl<string | null>;
    bank_pix: FormControl<string | null>;
    bank_holder: FormControl<string | null>;
    social_instagram: FormControl<string | null>;
    social_facebook: FormControl<string | null>;
    social_linkedin: FormControl<string | null>;
    emergency_contact_name: FormControl<string | null>;
    emergency_contact_phone: FormControl<string | null>;
    emergency_contact_relation: FormControl<string | null>;
    identity_documents: FormArray<DocRowForm>;
    labor_documents: FormArray<DocRowForm>;
    sale_comission: FormControl<number | null>;
    is_network?: FormControl<boolean | null>;
    is_admin: FormControl<boolean | null>;
    is_active: FormControl<boolean | null>;
    type_user: FormControl<EUserType | null>;
    permission_id: FormControl<number | null>;
    company_id: FormControl<number | null>;
}>;
@Component({
    selector: 'app-user-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatInputModule,
        RouterModule,
        MatIconModule,
        MatSelectModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MatTabsModule,
        NgxMaskDirective,
        ModuleAttachmentsComponent,
        UserDocumentSignaturesComponent,
    ],
    templateUrl: './user-form.component.html',
    styles: [
        `
            ::ng-deep .bank-filter-option.mdc-list-item--disabled {
                opacity: 1;
                position: sticky;
                top: 0;
                z-index: 2;
                background: inherit;
            }
            ::ng-deep .bank-filter-option .mdc-list-item__primary-text {
                opacity: 1;
                width: 100%;
            }
        `,
    ],
})
export class UserFormComponent implements OnInit {
    loggedUser: UserModel;
    form: UserForm = this.fb.group({
        user_id: [null],
        name: [null, [Validators.required]],
        phone: [null, [Validators.required]],
        phone_secondary: [null],
        photo_url: [null],
        birth_date: [null],
        marital_status: [null],
        email: [null, [Validators.required, Validators.email]],
        email_secondary: [null, [Validators.email]],
        password: [null],
        sector: [null],
        manager_user_id: [null],
        admission_date: [null],
        termination_date: [null],
        current_role_title: [{ value: null, disabled: true }],
        cep: [null],
        uf: [null],
        neighboorhod: [null],
        street: [null],
        number: [null],
        complement: [null],
        city: [null],
        observation: [null],
        type_doc: [EDocumentType.CPF, [Validators.required]],
        doc_number: [null, [Validators.required]],
        bank_name: [null],
        bank_agency: [null],
        bank_account: [null],
        bank_account_type: [null],
        bank_pix: [null],
        bank_holder: [null],
        social_instagram: [null],
        social_facebook: [null],
        social_linkedin: [null],
        emergency_contact_name: [null],
        emergency_contact_phone: [null],
        emergency_contact_relation: [null],
        identity_documents: this.fb.array<DocRowForm>([]),
        labor_documents: this.fb.array<DocRowForm>([]),
        sale_comission: [null],
        is_admin: [false],
        is_active: [true],
        type_user: [null],
        permission_id: [null, [Validators.required]],
        company_id: [null],
    }) as UserForm;

    loading: boolean = false;
    loadingData: boolean = false;
    uploadingPhoto = false;

    pendingPhoto: File | null = null;
    photoPreview: string | null = null;

    typeDocEnum = EDocumentType;
    typeDocOptions = EDocumentTypeOptions;
    maritalStatusOptions = MaritalStatusOptions;
    identityDocTypeOptions = UserIdentityDocumentTypeOptions;
    laborDocTypeOptions = UserLaborDocumentTypeOptions;
    bankAccountTypeOptions = BANK_ACCOUNT_TYPE_OPTIONS;
    bankOptions = BrazilianBankOptions;
    filteredBankOptions = BrazilianBankOptions;
    bankSearch = new FormControl<string>('');
    permissionOptions: PermissionModel[] = [];
    managerOptions: UserModel[] = [];

    /** Usuário do formulário é admin (para ocultar Empresa quando admin) */
    get isFormUserAdmin(): boolean {
        return this.form.controls.is_admin?.value === true;
    }

    /** Permissão selecionada tem is_seller (para exibir e validar Comissão) */
    get selectedPermissionIsSeller(): boolean {
        const pid = this.form.controls.permission_id?.value;
        return this.permissionOptions.find((p) => p.permission_id === pid)?.is_seller === true;
    }

    /** Permissão de nome "Admin" / "Administrador" (lista) — usada ao marcar É administrador */
    private findAdminPermission(): PermissionModel | undefined {
        const opts = this.permissionOptions;
        if (!opts?.length) return undefined;
        const norm = (s: string | null | undefined) => String(s || '').trim().toLowerCase();
        const exact = opts.find((p) => {
            const n = norm(p.item);
            return n === 'admin' || n === 'administrador';
        });
        if (exact) return exact;
        return opts.find((p) => {
            const n = norm(p.item);
            return n.includes('admin') || n.includes('administrador');
        });
    }

    companyOptions: CompanyModel[] = [];

    documentMask: string = '';

    searchingZipCode: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: UserModel | null,
        private fb: FormBuilder,
        private service: UserService,
        private route: ActivatedRoute,
        private dialogRef: MatDialogRef<UserFormComponent>,
        private permissionService: PermissionService,
        private companyService: CompanyService,
        private viaCepService: ViaCepService,
        private dialog: MatDialog,
        private toast: ToastService,
        private salaryHistoryService: UserSalaryHistoryService,
        private hourBankService: UserHourBankService,
        private warningService: UserWarningService,
        private fileModuleService: FileModuleService,
        private financeService: FinanceService,
        private imageViewerService: ImageViewerService,
        private s3: S3Service,
    ) {
        this.dialogRef.addPanelClass('card-dialog');
    }

    get identityDocuments(): FormArray<DocRowForm> {
        return this.form.controls.identity_documents;
    }

    get laborDocuments(): FormArray<DocRowForm> {
        return this.form.controls.labor_documents;
    }

    get ageYears(): number | null {
        const raw = this.form.controls.birth_date?.value;
        const dt = this.toDateTime(raw);
        if (!dt?.isValid) return null;
        return Math.floor(DateTime.now().diff(dt, 'years').years);
    }

    get photoDisplayUrl(): string | null {
        return this.photoPreview || this.form.controls.photo_url?.value || this.data?.photo_url || null;
    }

    // --- RH history tabs ---
    salaryHistory: UserSalaryHistoryModel[] = [];
    hourBank: UserHourBankModel[] = [];
    warnings: UserWarningModel[] = [];
    /** Anexo 1:1 por registro (entity_id → file_module). */
    hourBankAttachmentById: Record<number, FileModuleModel> = {};
    warningAttachmentById: Record<number, FileModuleModel> = {};
    employeeFinances: FinanceModel[] = [];
    loadingSalaryHistory = false;
    loadingHourBank = false;
    loadingWarnings = false;
    loadingEmployeeFinances = false;
    salaryHistoryLoaded = false;
    hourBankLoaded = false;
    warningsLoaded = false;
    employeeFinancesLoaded = false;

    onUserTabIndexChange(index: number): void {
        // Geral=0, Anexos=1, Assinaturas=2, Salários=3, Banco horas=4, Advertências=5, Financeiro=6
        if (index === 3) this.ensureSalaryHistoryLoaded();
        if (index === 4) this.ensureHourBankLoaded();
        if (index === 5) this.ensureWarningsLoaded();
        if (index === 6) this.ensureEmployeeFinancesLoaded();
    }

    ensureSalaryHistoryLoaded(): void {
        if (!this.salaryHistoryLoaded && this.data?.user_id) this.loadSalaryHistory();
    }

    ensureHourBankLoaded(): void {
        if (!this.hourBankLoaded && this.data?.user_id) this.loadHourBank();
    }

    ensureWarningsLoaded(): void {
        if (!this.warningsLoaded && this.data?.user_id) this.loadWarnings();
    }

    ensureEmployeeFinancesLoaded(): void {
        if (!this.employeeFinancesLoaded && this.data?.user_id) this.loadEmployeeFinances();
    }

    loadSalaryHistory(): void {
        if (!this.data?.user_id) return;
        this.loadingSalaryHistory = true;
        this.salaryHistoryService
            .list({ user_id: this.data.user_id, limit: 'all', page: 1 } as any)
            .pipe(take(1), finalize(() => (this.loadingSalaryHistory = false)))
            .subscribe({
                next: (res) => {
                    this.salaryHistory = (res.items || []).map((i) => new UserSalaryHistoryModel(i));
                    this.salaryHistoryLoaded = true;
                },
                error: () => this.toast.error('Erro ao carregar histórico de salários.'),
            });
    }

    loadHourBank(): void {
        if (!this.data?.user_id) return;
        this.loadingHourBank = true;
        this.hourBankService
            .list({ user_id: this.data.user_id, limit: 'all', page: 1 } as any)
            .pipe(take(1), finalize(() => (this.loadingHourBank = false)))
            .subscribe({
                next: async (res) => {
                    this.hourBank = (res.items || []).map((i) => new UserHourBankModel(i));
                    this.hourBankLoaded = true;
                    await this.loadRhAttachments(
                        'USER_HOUR_BANK',
                        this.hourBank.map((i) => i.user_hour_bank_id).filter(Boolean),
                        (map) => (this.hourBankAttachmentById = map),
                    );
                },
                error: () => this.toast.error('Erro ao carregar banco de horas.'),
            });
    }

    loadWarnings(): void {
        if (!this.data?.user_id) return;
        this.loadingWarnings = true;
        this.warningService
            .list({ user_id: this.data.user_id, limit: 'all', page: 1 } as any)
            .pipe(take(1), finalize(() => (this.loadingWarnings = false)))
            .subscribe({
                next: async (res) => {
                    this.warnings = (res.items || []).map((i) => new UserWarningModel(i));
                    this.warningsLoaded = true;
                    await this.loadRhAttachments(
                        'USER_WARNING',
                        this.warnings.map((i) => i.user_warning_id).filter(Boolean),
                        (map) => (this.warningAttachmentById = map),
                    );
                },
                error: () => this.toast.error('Erro ao carregar advertências.'),
            });
    }

    private async loadRhAttachments(
        module: 'USER_WARNING' | 'USER_HOUR_BANK',
        entityIds: number[],
        assign: (map: Record<number, FileModuleModel>) => void,
    ): Promise<void> {
        const map: Record<number, FileModuleModel> = {};
        if (!entityIds.length) {
            assign(map);
            return;
        }
        await Promise.all(
            entityIds.map(async (entityId) => {
                try {
                    // Não passar sort: o findMany aplica snakeCase e quebra (createdAt → created_at).
                    const resp = await firstValueFrom(
                        this.fileModuleService.list({
                            module,
                            entity_id: entityId,
                            type_file: 'Documento',
                            limit: 'all',
                            page: 1,
                        } as any),
                    );
                    const items = (resp?.items || []).map((i) => new FileModuleModel(i));
                    items.sort((a, b) => {
                        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                        return tb - ta;
                    });
                    const file = items[0];
                    if (file) map[entityId] = file;
                } catch {
                    // ignora falha pontual
                }
            }),
        );
        assign(map);
    }

    downloadRhAttachment(file: FileModuleModel | undefined, event?: Event): void {
        event?.stopPropagation();
        if (!file?.path) {
            this.toast.warning('Nenhum anexo disponível');
            return;
        }
        this.imageViewerService.openInNewTab(file.path);
    }

    openSalaryHistoryForm(item?: UserSalaryHistoryModel): void {
        if (!this.data?.user_id) return;
        this.dialog
            .open(UserSalaryHistoryFormComponent, {
                width: '560px',
                maxHeight: '90vh',
                data: { item: item ?? null, user_id: this.data.user_id } as UserSalaryHistoryFormDialogData,
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((changed) => {
                if (changed) {
                    this.salaryHistoryLoaded = false;
                    this.loadSalaryHistory();
                }
            });
    }

    openHourBankForm(item?: UserHourBankModel): void {
        if (!this.data?.user_id) return;
        this.dialog
            .open(UserHourBankFormComponent, {
                width: '640px',
                maxHeight: '90vh',
                data: { item: item ?? null, user_id: this.data.user_id } as UserHourBankFormDialogData,
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((changed) => {
                if (changed) {
                    this.hourBankLoaded = false;
                    this.loadHourBank();
                }
            });
    }

    openWarningForm(item?: UserWarningModel): void {
        if (!this.data?.user_id) return;
        this.dialog
            .open(UserWarningFormComponent, {
                width: '640px',
                maxHeight: '90vh',
                data: { item: item ?? null, user_id: this.data.user_id } as UserWarningFormDialogData,
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((changed) => {
                if (changed) {
                    this.warningsLoaded = false;
                    this.loadWarnings();
                }
            });
    }

    deleteSalaryHistory(item: UserSalaryHistoryModel, event?: Event): void {
        event?.stopPropagation();
        this.dialog
            .open(ConfirmDialogComponent, {
                data: {
                    title: 'Remover registro',
                    message: 'Deseja remover este registro de salário/cargo?',
                    confirmText: 'Remover',
                    cancelText: 'Cancelar',
                } as ConfirmDialogData,
                width: '480px',
                panelClass: 'card-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((ok) => {
                if (!ok) return;
                this.salaryHistoryService
                    .delete(item)
                    .pipe(take(1))
                    .subscribe({
                        next: () => {
                            this.toast.success('Registro removido.');
                            this.salaryHistoryLoaded = false;
                            this.loadSalaryHistory();
                        },
                        error: () => this.toast.error('Erro ao remover.'),
                    });
            });
    }

    deleteHourBank(item: UserHourBankModel, event?: Event): void {
        event?.stopPropagation();
        this.dialog
            .open(ConfirmDialogComponent, {
                data: {
                    title: 'Remover lançamento',
                    message: 'Deseja remover este lançamento do banco de horas?',
                    confirmText: 'Remover',
                    cancelText: 'Cancelar',
                } as ConfirmDialogData,
                width: '480px',
                panelClass: 'card-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((ok) => {
                if (!ok) return;
                this.hourBankService
                    .delete(item)
                    .pipe(take(1))
                    .subscribe({
                        next: () => {
                            this.toast.success('Lançamento removido.');
                            this.hourBankLoaded = false;
                            this.loadHourBank();
                        },
                        error: () => this.toast.error('Erro ao remover.'),
                    });
            });
    }

    deleteWarning(item: UserWarningModel, event?: Event): void {
        event?.stopPropagation();
        this.dialog
            .open(ConfirmDialogComponent, {
                data: {
                    title: 'Remover advertência',
                    message: 'Deseja remover esta advertência?',
                    confirmText: 'Remover',
                    cancelText: 'Cancelar',
                } as ConfirmDialogData,
                width: '480px',
                panelClass: 'card-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((ok) => {
                if (!ok) return;
                this.warningService
                    .delete(item)
                    .pipe(take(1))
                    .subscribe({
                        next: () => {
                            this.toast.success('Advertência removida.');
                            this.warningsLoaded = false;
                            this.loadWarnings();
                        },
                        error: () => this.toast.error('Erro ao remover.'),
                    });
            });
    }

    formatBrl(n: number): string {
        return Number(n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    formatHours(n: number): string {
        const value = Number(n ?? 0);
        return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}h`;
    }

    /** Banco de horas: Entrada = CREDIT, Saída = DEBIT, Saldo = entrada − saída */
    get hourBankKpis(): { balance: number; entrada: number; saida: number } {
        let entrada = 0;
        let saida = 0;
        for (const item of this.hourBank) {
            const hours = Number(item.hours) || 0;
            if (item.entry_type === 'DEBIT') saida += hours;
            else entrada += hours;
        }
        return { entrada, saida, balance: entrada - saida };
    }

    /**
     * Financeiro na ótica do funcionário:
     * Entrada = SAIDA da empresa (salário/bônus), Saída = ENTRADA da empresa (desconto).
     */
    get employeeFinanceKpis(): { balance: number; entrada: number; saida: number } {
        let entrada = 0;
        let saida = 0;
        for (const item of this.employeeFinances) {
            const value = Number(item.value) || 0;
            if (item.financial_account?.operation === 'ENTRADA') saida += value;
            else entrada += value;
        }
        return { entrada, saida, balance: entrada - saida };
    }

    formatDateOnly(v: string | Date | null): string {
        if (!v) return '—';
        const s = typeof v === 'string' ? v.slice(0, 10) : null;
        if (s) {
            const [y, m, d] = s.split('-');
            return `${d}/${m}/${y}`;
        }
        const d = v as Date;
        return d.toLocaleDateString('pt-BR');
    }

    hourBankTypeLabel(t: string): string {
        return t === 'DEBIT' ? 'Débito' : 'Crédito';
    }

    warningSeverityLabel(s: string): string {
        return ({ LOW: 'Leve', MEDIUM: 'Média', HIGH: 'Grave' } as Record<string, string>)[s] || s;
    }

    loadEmployeeFinances(): void {
        if (!this.data?.user_id) return;
        this.loadingEmployeeFinances = true;
        this.financeService
            .list({
                user_receiver_id: this.data.user_id,
                financial_account_module: 'OUTROS',
                limit: 'all',
                page: 1,
                sort: 'date',
                direction: 'DESC',
            } as any)
            .pipe(
                take(1),
                finalize(() => (this.loadingEmployeeFinances = false)),
            )
            .subscribe({
                next: (res) => {
                    this.employeeFinances = (res.items || []).map((i) => new FinanceModel(i));
                    this.employeeFinancesLoaded = true;
                },
                error: () => this.toast.error('Erro ao carregar lançamentos financeiros.'),
            });
    }

    openEmployeeFinanceForm(item?: FinanceModel): void {
        if (!this.data?.user_id) return;
        this.dialog
            .open(FinanceFormComponent, {
                width: '700px',
                maxHeight: '90vh',
                data: item?.finance_id
                    ? { ...item, forceOutrosModule: true }
                    : {
                          forceOutrosModule: true,
                          sale_id: null,
                          user_receiver_id: this.data.user_id,
                          user_payer_id: null,
                      },
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((changed) => {
                if (changed) {
                    this.employeeFinancesLoaded = false;
                    this.toast.success('Lançamento financeiro salvo.');
                    this.loadEmployeeFinances();
                }
            });
    }

    deleteEmployeeFinance(item: FinanceModel, event?: Event): void {
        event?.stopPropagation();
        if (!item?.finance_id) return;
        this.dialog
            .open(ConfirmDialogComponent, {
                data: {
                    title: 'Remover lançamento',
                    message: 'Deseja remover este lançamento financeiro?',
                    confirmText: 'Remover',
                    cancelText: 'Cancelar',
                } as ConfirmDialogData,
                width: '480px',
                panelClass: 'card-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((ok) => {
                if (!ok) return;
                this.financeService
                    .deleteFinance(item.finance_id)
                    .pipe(take(1))
                    .subscribe({
                        next: () => {
                            this.toast.success('Lançamento removido.');
                            this.employeeFinancesLoaded = false;
                            this.loadEmployeeFinances();
                        },
                        error: () => this.toast.error('Erro ao remover lançamento.'),
                    });
            });
    }

    formatFinanceDate(v: string | Date | null): string {
        if (!v) return '—';
        const d = v instanceof Date ? v : new Date(v);
        if (Number.isNaN(d.getTime())) {
            const s = String(v).slice(0, 10);
            const [y, m, day] = s.split('-');
            return y && m && day ? `${day}/${m}/${y}` : '—';
        }
        return d.toLocaleDateString('pt-BR');
    }

    financeStatusLabel(item: FinanceModel): string {
        return financialStatusRecord[item.status] || item.status || '—';
    }

    get canEditSystemConfig(): boolean {
        // Admin sempre pode editar configurações do sistema
        if (this.loggedUser?.isAdmin) {
            return true;
        }

        // Se não for admin e estiver editando o próprio perfil, não pode editar
        if (this.loggedUser && this.data?.user_id && this.loggedUser.user_id === this.data.user_id) {
            return false;
        }

        // Para novos usuários ou edição de outros usuários por não-admin (improvável), permitir
        return true;
    }

    ngOnInit(): void {
        ApiService.userCurrent
            .pipe(
                map((user) => new UserModel(user)),
                tap((user) => {
                    this.loggedUser = user;
                }),
                take(1),
            )
            .subscribe();
        this.getUser();
        this.getPermissionOptions();
        this.getCompanyOptions();
        this.getManagerOptions();
        this.bankSearch.valueChanges.subscribe((term) => this.applyBankFilter(term));

        this.configForm();
    }

    onBankSelectOpened(open: boolean, input?: HTMLInputElement): void {
        if (open) {
            setTimeout(() => input?.focus());
            return;
        }
        this.bankSearch.setValue('', { emitEvent: false });
        this.filteredBankOptions = this.bankOptions;
    }

    private applyBankFilter(term: string | null): void {
        const query = (term || '').trim().toLowerCase();
        const selected = this.form.controls.bank_name.value;
        this.filteredBankOptions = !query
            ? this.bankOptions
            : this.bankOptions.filter(
                  (opt) =>
                      opt.value === selected ||
                      opt.label.toLowerCase().includes(query) ||
                      opt.value.includes(query),
              );
    }

    configForm(): void {
        this.form.controls.permission_id?.valueChanges.subscribe(() => {
            this.updatePasswordValidation();
        });

        this.form.controls.type_doc?.valueChanges.subscribe((documentType) => {
            this.updateDocumentValidation(documentType);
        });

        this.form.controls.is_admin?.valueChanges.subscribe(() => {
            this.updatePermissionAndAdminValidation();
        });
        this.form.controls.permission_id?.valueChanges.subscribe(() => {
            this.updatePermissionAndAdminValidation();
        });

        this.form.controls.doc_number.disable();

        if (this.data) {
            this.patchHrProfile(this.data);
            this.form.controls.is_admin?.setValue(this.data.type_user === EUserType.ADMIN, { emitEvent: false });
            this.form.updateValueAndValidity();
            if (typeof (this.data as any).is_network !== 'undefined') {
                const val = Number((this.data as any).is_network) === 1;
                (this.form.controls as any).is_network?.setValue(val, { emitEvent: false });
            }
            this.updatePermissionAndAdminValidation();
        } else if (this.identityDocuments.length === 0) {
            this.addIdentityDocument(EUserDocumentType.CPF);
        }

        if (!this.loggedUser.isAdmin) this.form.controls.company_id.setValue(this.loggedUser.company.company_id);

        if (!this.canEditSystemConfig) {
            this.form.controls.is_admin.disable();
            this.form.controls.permission_id.disable();
            this.form.controls.company_id.disable();
            this.form.controls.sale_comission.disable();
        }
    }

    async done(): Promise<void> {
        this.syncPrimaryDocFromDocuments();
        this.form.markAllAsTouched();
        this.identityDocuments.markAllAsTouched();
        this.laborDocuments.markAllAsTouched();

        if (!this.form.valid || !this.service) return;

        this.loading = true;

        try {
            if (this.pendingPhoto) {
                this.uploadingPhoto = true;
                const url = await this.s3.uploadFile(this.pendingPhoto, 'users/photos');
                this.form.controls.photo_url.setValue(url);
                this.pendingPhoto = null;
                this.uploadingPhoto = false;
            }
        } catch {
            this.loading = false;
            this.uploadingPhoto = false;
            this.toast.error('Falha ao enviar a foto do usuário.');
            return;
        }

        const formData = this.form.getRawValue();
        const data = formData as any;

        const isAdmin = data.is_admin === true;
        if (isAdmin) {
            data.type_user = EUserType.ADMIN;
            data.permission_id = this.findAdminPermission()?.permission_id ?? data.permission_id ?? null;
        } else {
            const perm = this.permissionOptions.find((p) => p.permission_id === data.permission_id);
            if (perm?.is_seller) data.type_user = EUserType.SELLER;
            else if (perm?.is_customer) data.type_user = EUserType.CUSTOMER;
            else data.type_user = EUserType.PRODUCTION;
        }

        {
            let pid = data.permission_id;
            if (pid === '' || pid === undefined) pid = null;
            if (pid != null) pid = Number(pid);
            data.permission_id = pid != null && !Number.isNaN(pid) ? pid : null;
        }

        data.doc_number = String(data.doc_number || '').replace(/[.\-\/]/g, '');
        data.documents = this.collectDocumentsPayload();
        data.birth_date = this.toIsoDate(data.birth_date);
        data.admission_date = this.toIsoDate(data.admission_date);
        data.termination_date = this.toIsoDate(data.termination_date);
        data.social_links = this.buildSocialLinksPayload(data);
        data.email_secondary = data.email_secondary || null;
        data.phone_secondary = data.phone_secondary || null;
        data.photo_url = data.photo_url || null;
        data.marital_status = data.marital_status || null;
        data.sector = data.sector || null;
        data.manager_user_id = data.manager_user_id != null ? Number(data.manager_user_id) : null;
        data.complement = data.complement || null;
        data.emergency_contact_name = data.emergency_contact_name || null;
        data.emergency_contact_phone = data.emergency_contact_phone || null;
        data.emergency_contact_relation = data.emergency_contact_relation || null;
        data.bank_name = data.bank_name || null;
        data.bank_agency = data.bank_agency || null;
        data.bank_account = data.bank_account || null;
        data.bank_account_type = data.bank_account_type || null;
        data.bank_pix = data.bank_pix || null;
        data.bank_holder = data.bank_holder || null;

        delete data.identity_documents;
        delete data.labor_documents;
        delete data.social_instagram;
        delete data.social_facebook;
        delete data.social_linkedin;
        delete data.current_role_title;
        delete data.is_admin;

        if (typeof data.is_network !== 'undefined') {
            data.is_network = !!data.is_network;
        }

        data.is_active = data.is_active !== false;

        if (this.data?.user_id) {
            const updatePayload = {
                ...data,
                permission_id: data.permission_id ?? this.form.getRawValue().permission_id ?? null,
            };
            if (!updatePayload.password || !String(updatePayload.password).trim()) {
                delete updatePayload.password;
            }
            this.service
                .update(updatePayload as UserModel)
                .pipe(
                    take(1),
                    finalize(() => (this.loading = false)),
                )
                .subscribe(() => this.dialogRef.close(true));
        } else {
            const cleanDoc = data.doc_number;
            const email = (data.email || '').trim().toLowerCase() || undefined;
            this.service.api
                .getSub<any>('user/list/deleted', this.buildCompositeLookupFilter({ doc_number: cleanDoc, email }))
                .pipe(take(1))
                .subscribe({
                    next: (resp) => {
                        const existing = this.findMatchingCompositeUser(resp?.items);
                        if (!existing) {
                            this.loading = true;
                            this.service
                                .create(data as UserModel)
                                .pipe(
                                    take(1),
                                    finalize(() => (this.loading = false)),
                                )
                                .subscribe(() => this.dialogRef.close(true));
                            return;
                        }

                        if (!existing.deletedAt) {
                            this.loading = false;
                            this.toast.warning('Usuário já existente e ativo para o documento informado');
                            return;
                        }

                        this.loading = false;
                        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                            data: {
                                title: 'Existe um usuário com este documento desativado',
                                message: 'Deseja reativá-lo e aplicar os dados preenchidos?',
                                confirmText: 'Reativar e aplicar dados',
                                cancelText: 'Cancelar',
                            } as ConfirmDialogData,
                        });

                        dialogRef.afterClosed().subscribe((confirmed) => {
                            if (!confirmed) return;
                            this.fillFormWithExisting(existing as UserModel);
                            this.form.controls.user_id.setValue(existing.user_id);
                            this.loading = true;
                            this.service
                                .restore(existing.user_id)
                                .pipe(take(1), finalize(() => (this.loading = false)))
                                .subscribe({
                                    next: () => {
                                        this.toast.success('Usuário reativado');
                                    },
                                    error: (err) => {
                                        this.toast.error('Falha ao reativar usuário');
                                        console.error(err);
                                    },
                                });
                        });
                    },
                    error: (error) => {
                        this.loading = false;
                        console.error('Erro ao validar documento', error);
                        this.toast.error('Erro ao validar documento do usuário');
                    },
                });
        }
    }

    clear(): void {
        this.dialogRef.close(false);
    }

    deleteUser(): void {
        if (this.data && this.service)
            this.service
                .delete(this.data)
                .pipe(
                    take(1),
                    finalize(() => (this.loading = false)),
                )
                .subscribe(() => this.dialogRef.close(true));
    }

    getPermissionOptions(): void {
        if (this.permissionService)
            this.permissionService
                .list({ limit: 'all' })
                .pipe(take(1))
                .subscribe((data) => {
                    this.permissionOptions = data.items.filter((permission) => !permission.is_customer);
                    this.updatePermissionAndAdminValidation();
                    this.updatePasswordValidation();
                });
    }

    getCompanyOptions(): void {
        if (this.companyService)
            this.companyService
                .list({ limit: 'all' })
                .pipe(take(1))
                .subscribe((data) => {
                    this.companyOptions = data.items;
                });
    }

    getManagerOptions(): void {
        this.service
            .listInternalUsers({ limit: 'all', page: 1 } as any)
            .pipe(take(1))
            .subscribe({
                next: (res) => {
                    const currentId = this.data?.user_id ?? null;
                    this.managerOptions = (res.items || [])
                        .map((u) => new UserModel(u))
                        .filter((u) => u.user_id !== currentId);
                },
                error: () => {
                    this.managerOptions = [];
                },
            });
    }

    getUser(): void {
        const id = this.data?.user_id;
        if (!id || !this.service) return;

        this.loadingData = true;
        this.service
            .getById(+id)
            .pipe(
                take(1),
                finalize(() => (this.loadingData = false)),
            )
            .subscribe((data) => {
                this.data = data;
                this.patchHrProfile(data);
                this.updatePasswordValidation();

                if (data.type_doc) this.updateDocumentValidation(data.type_doc);

                this.form.controls.is_admin?.setValue(data.type_user === EUserType.ADMIN, { emitEvent: false });
                this.updatePermissionAndAdminValidation();

                if (data.cep) this.searchZipCode();
            });
    }

    updatePasswordValidation(): void {
        const permissionId = this.form.controls.permission_id?.value;
        const passwordControl = this.form.controls.password;
        const isEdit = !!this.data?.user_id;

        if (this.permissionOptions.length > 0 && permissionId) {
            const selectedPermission = this.permissionOptions.find((p) => p.permission_id === permissionId);
            // Senha obrigatória apenas na criação E se pode fazer login
            // Na edição, senha é opcional (preencher = alterar senha)
            if (selectedPermission?.can_login && !isEdit) {
                passwordControl?.setValidators([Validators.required]);
            } else {
                passwordControl?.clearValidators();
            }
        } else {
            passwordControl?.clearValidators();
        }

        passwordControl?.updateValueAndValidity();
    }

    updatePermissionAndAdminValidation(): void {
        const isAdmin = this.form.controls.is_admin?.value === true;
        const permissionControl = this.form.controls.permission_id;
        const saleCommissionControl = this.form.controls.sale_comission;
        const companyControl = this.form.controls.company_id;
        const isNetworkControl = (this.form.controls as any).is_network as FormControl | undefined;

        // Admin: exibir e travar permissão "Admin"
        if (isAdmin) {
            permissionControl?.clearValidators();
            const adminPerm = this.findAdminPermission();
            if (adminPerm) {
                permissionControl?.setValue(adminPerm.permission_id, { emitEvent: false });
            } else {
                permissionControl?.setValue(null, { emitEvent: false });
            }
            if (this.canEditSystemConfig) {
                permissionControl?.disable({ emitEvent: false });
            }
        } else {
            if (this.canEditSystemConfig) {
                permissionControl?.enable({ emitEvent: false });
            }
            const adminPid = this.findAdminPermission()?.permission_id;
            if (adminPid != null && permissionControl?.value === adminPid) {
                permissionControl?.setValue(null, { emitEvent: false });
            }
            permissionControl?.setValidators([Validators.required]);
        }

        const effectivePermissionId = permissionControl?.value;
        const perm = this.permissionOptions.find((p) => p.permission_id === effectivePermissionId);

        if (isAdmin) {
            saleCommissionControl?.clearValidators();
            saleCommissionControl?.reset();
        } else if (perm?.is_seller) {
            saleCommissionControl?.setValidators([Validators.required, Validators.min(0)]);
        } else {
            saleCommissionControl?.clearValidators();
            if (perm !== undefined) {
                saleCommissionControl?.reset();
            }
        }

        if (!isAdmin) {
            companyControl?.setValidators([Validators.required]);
        } else {
            companyControl?.clearValidators();
        }

        if (isNetworkControl) {
            if (!isAdmin && perm?.is_customer) {
                isNetworkControl.enable({ emitEvent: false });
            } else {
                isNetworkControl.disable({ emitEvent: false });
                isNetworkControl.setValue(false, { emitEvent: false });
            }
        }

        [permissionControl, saleCommissionControl, companyControl, isNetworkControl].forEach((c) => c?.updateValueAndValidity());
    }

    updateDocumentValidation(documentType: EDocumentType): void {
        const docNumberControl = this.form.controls.doc_number;

        if (!documentType || !docNumberControl) {
            this.clearDocumentValidation();
            return;
        }

        this.documentMask = DocumentUtils.getMask(documentType);

        const validators = [Validators.required, CustomValidator.documentValidator(documentType)];
        docNumberControl.setValidators(validators);

        docNumberControl.enable();
        docNumberControl.updateValueAndValidity();
    }

    docCheckLoading: boolean = false;
    docExistsActive: boolean = false;
    docExistsDeleted: boolean = false;
    docCheckError: boolean = false;
    docExistingUser: UserModel | null = null;
    reactivatePromptShown: boolean = false;

    emailCheckLoading: boolean = false;
    emailExistsActive: boolean = false;
    emailExistsDeleted: boolean = false;
    emailCheckError: boolean = false;
    emailExistingUser: UserModel | null = null;

    private normalizeDoc(doc: string): string {
        return String(doc || '').replace(/\D/g, '');
    }

    private expectedLen(documentType: EDocumentType | null): number {
        if (documentType === EDocumentType.CPF) return 11;
        if (documentType === EDocumentType.CNPJ) return 14;
        return 0;
    }

    private canCheckDoc(): { type: EDocumentType | null; clean: string } | null {
        const type = this.form.controls.type_doc.value;
        const val = this.form.controls.doc_number.value;
        const clean = this.normalizeDoc(val || '');
        const len = this.expectedLen(type);
        if (!type || !len || clean.length !== len) return null;
        const isValid = DocumentUtils.validateDocument(clean, type);
        if (!isValid) return null;
        return { type, clean };
    }

    onDocInput(): void {
        const payload = this.canCheckDoc();
        if (!payload) {
            this.docCheckLoading = false;
            this.docExistsActive = false;
            this.docExistsDeleted = false;
            this.docCheckError = false;
            this.docExistingUser = null;
            this.reactivatePromptShown = false;
            return;
        }
        this.checkDocNumber(payload.clean);
    }

    onDocBlur(): void {
        const payload = this.canCheckDoc();
        if (!payload) return;
        this.checkDocNumber(payload.clean, true);
    }

    private checkDocNumber(cleanDoc: string, prompt: boolean = false): void {
        this.docCheckLoading = true;
        this.docCheckError = false;
        this.docExistsActive = false;
        this.docExistsDeleted = false;
        this.docExistingUser = null;
        this.service.api
            .getSub<any>('user/list/deleted', this.buildCompositeLookupFilter({ doc_number: cleanDoc }))
            .pipe(take(1))
            .subscribe({
                next: (resp) => {
                    const existing = this.findMatchingCompositeUser(resp?.items);
                    this.docCheckLoading = false;
                    if (!existing) return;
                    this.docExistingUser = existing as UserModel;
                    const isActive = !existing.deletedAt;
                    this.docExistsActive = !!isActive;
                    this.docExistsDeleted = !isActive;
                    if (prompt && this.docExistsDeleted && !this.reactivatePromptShown) {
                        this.reactivatePromptShown = true;
                        this.promptReactivate();
                    }
                },
                error: () => {
                    this.docCheckLoading = false;
                    this.docCheckError = true;
                },
            });
    }

    private promptReactivate(): void {
        const existing = this.docExistingUser;
        if (!existing || !this.docExistsDeleted) return;
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Existe um usuário com este documento desativado',
                message: 'Deseja reativá-lo e aplicar os dados preenchidos?',
                confirmText: 'Reativar e aplicar dados',
                cancelText: 'Cancelar',
            } as ConfirmDialogData,
            width: '600px',
            maxWidth: '100vw',
            panelClass: 'card-dialog',
        });
        dialogRef.afterClosed().subscribe((confirmed) => {
            if (!confirmed) return;
            this.fillFormWithExisting(existing as UserModel);
            this.form.controls.user_id.setValue(existing.user_id);
            this.loading = true;
            this.service
                .restore(existing.user_id)
                .pipe(take(1), finalize(() => (this.loading = false)))
                .subscribe({
                    next: () => {
                        this.toast.success('Usuário reativado');
                    },
                    error: () => {
                        this.toast.error('Falha ao reativar usuário');
                    },
                });
        });
    }

    private deriveTypeUserFromForm(): EUserType {
        const formData = this.form.getRawValue();
        if (formData.is_admin) return EUserType.ADMIN;
        const perm = this.permissionOptions.find((p) => p.permission_id === formData.permission_id);
        if (perm?.is_seller) return EUserType.SELLER;
        if (perm?.is_customer) return EUserType.CUSTOMER;
        return EUserType.PRODUCTION;
    }

    private buildCompositeLookupFilter(extra: { email?: string; doc_number?: string }): Record<string, unknown> {
        const formData = this.form.getRawValue();
        const companyId = formData.company_id ?? this.loggedUser?.company?.company_id ?? null;
        const filter: Record<string, unknown> = { limit: 10, ...extra };
        if (companyId != null) filter.company_id = companyId;
        filter.type_user = this.deriveTypeUserFromForm();
        const cleanDoc = this.normalizeDoc(formData.doc_number || '');
        if (cleanDoc) filter.doc_number = cleanDoc;
        return filter;
    }

    private findMatchingCompositeUser(items: UserModel[] | undefined): UserModel | null {
        if (!items?.length) return null;
        const formData = this.form.getRawValue();
        const companyId = formData.company_id ?? this.loggedUser?.company?.company_id ?? null;
        const email = (formData.email || '').trim().toLowerCase() || null;
        const typeUser = this.deriveTypeUserFromForm();
        const docNumber = this.normalizeDoc(formData.doc_number || '') || null;
        return (
            items.find((user) => {
                const userCompanyId = user.company_id ?? user.company?.company_id ?? null;
                const userEmail = (user.email || '').trim().toLowerCase() || null;
                return (
                    userCompanyId === companyId &&
                    userEmail === email &&
                    user.type_user === typeUser &&
                    (user.doc_number ?? null) === docNumber
                );
            }) ?? null
        );
    }

    private canCheckEmail(): string | null {
        const val = (this.form.controls.email.value || '').trim();
        const ctrl = this.form.controls.email;
        if (!val || !ctrl || !ctrl.valid) return null;
        return val.toLowerCase();
    }

    onEmailInput(): void {
        const email = this.canCheckEmail();
        if (!email) {
            this.emailCheckLoading = false;
            this.emailExistsActive = false;
            this.emailExistsDeleted = false;
            this.emailCheckError = false;
            this.emailExistingUser = null;
            return;
        }
        this.checkEmail(email);
    }

    onEmailBlur(): void {
        const email = this.canCheckEmail();
        if (!email) return;
        this.checkEmail(email, true);
    }

    private checkEmail(email: string, prompt: boolean = false): void {
        this.emailCheckLoading = true;
        this.emailCheckError = false;
        this.emailExistsActive = false;
        this.emailExistsDeleted = false;
        this.emailExistingUser = null;
        this.service.api
            .getSub<any>('user/list/deleted', this.buildCompositeLookupFilter({ email }))
            .pipe(take(1))
            .subscribe({
                next: (resp) => {
                    const existing = this.findMatchingCompositeUser(resp?.items);
                    this.emailCheckLoading = false;
                    if (!existing) return;
                    this.emailExistingUser = existing as UserModel;
                    const isActive = !existing.deletedAt;
                    this.emailExistsActive = !!isActive;
                    this.emailExistsDeleted = !isActive;
                    if (prompt && this.emailExistsDeleted && !this.reactivatePromptShown) {
                        this.reactivatePromptShown = true;
                        this.promptReactivateEmail();
                    }
                },
                error: () => {
                    this.emailCheckLoading = false;
                    this.emailCheckError = true;
                },
            });
    }

    private promptReactivateEmail(): void {
        const existing = this.emailExistingUser;
        if (!existing || !this.emailExistsDeleted) return;
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Existe um email com este documento desativado',
                message: 'Deseja reativá-lo e aplicar os dados preenchidos?',
                confirmText: 'Reativar e aplicar dados',
                cancelText: 'Cancelar',
            } as ConfirmDialogData,
            width: '600px',
            maxWidth: '100vw',
            panelClass: 'card-dialog',
        });
        dialogRef.afterClosed().subscribe((confirmed) => {
            if (!confirmed) return;
            this.fillFormWithExisting(existing as UserModel);
            this.form.controls.user_id.setValue(existing.user_id);
            this.loading = true;
            this.service
                .restore(existing.user_id)
                .pipe(take(1), finalize(() => (this.loading = false)))
                .subscribe({
                    next: () => {
                        this.toast.success('Usuário reativado');
                    },
                    error: () => {
                        this.toast.error('Falha ao reativar usuário');
                    },
                });
        });
    }

    private clearDocumentValidation(): void {
        const docNumberControl = this.form.controls.doc_number;
        if (docNumberControl) {
            docNumberControl.setValidators([Validators.required]);
            docNumberControl.disable();
            docNumberControl.updateValueAndValidity();
        }

        this.documentMask = '';
    }

    searchZipCode(): void {
        const zipCode = this.form.controls.cep?.value;
        if (!this.isValidZipCode(zipCode)) return this.clearAddressFields();

        this.searchingZipCode = true;
        this.viaCepService
            .searchZipCode(zipCode)
            .pipe(
                take(1),
                finalize(() => (this.searchingZipCode = false)),
            )
            .subscribe({
                next: (addressData) => this.handleAddressData(addressData),
                error: () => this.clearAddressFields(),
            });
    }

    private isValidZipCode(zipCode: string | null): boolean {
        return !!(zipCode && this.viaCepService.validateZipCode(zipCode));
    }

    private handleAddressData(addressData: ICepResponse | null): void {
        if (!addressData) {
            this.clearAddressFields();
            return;
        }

        this.form.patchValue(
            {
                street: addressData.street,
                neighboorhod: addressData.neighborhood,
                city: addressData.city,
                uf: addressData.state,
            },
            { emitEvent: false },
        );

        this.disableFilledAddressFields(addressData);
    }

    private disableFilledAddressFields(addressData: ICepResponse): void {
        const fieldMappings = [
            { data: addressData.street, control: this.form.controls.street },
            { data: addressData.neighborhood, control: this.form.controls.neighboorhod },
            { data: addressData.city, control: this.form.controls.city },
            { data: addressData.state, control: this.form.controls.uf },
        ];

        fieldMappings.forEach(({ data, control }) => {
            if (data) {
                control?.disable({ emitEvent: false });
            }
        });
    }

    private clearAddressFields(): void {
        this.form.patchValue(
            {
                street: '',
                neighboorhod: '',
                city: '',
                uf: '',
            },
            { emitEvent: false },
        );

        const addressControls = [
            this.form.controls.cep,
            this.form.controls.street,
            this.form.controls.neighboorhod,
            this.form.controls.city,
            this.form.controls.uf,
        ];

        addressControls.forEach((control) => control?.enable({ emitEvent: false }));
    }

    private fillFormWithExisting(existing: UserModel): void {
        const company_id = existing?.company?.company_id ?? null;
        const permission_id = existing?.permission?.permission_id ?? null;
        const is_network_ctrl = (this.form.controls as any).is_network;
        this.patchHrProfile(existing);
        this.form.patchValue(
            {
                user_id: existing.user_id,
                sale_comission: existing.sale_comission ?? null,
                type_user: existing.type_user ?? null,
                is_active: existing.is_active !== false,
                permission_id: permission_id,
                company_id: company_id,
            } as any,
            { emitEvent: false },
        );
        if (is_network_ctrl) {
            is_network_ctrl.setValue(!!existing.is_network, { emitEvent: false });
        }
        if (existing.type_doc) this.updateDocumentValidation(existing.type_doc);
        this.form.controls.is_admin?.setValue(existing.type_user === EUserType.ADMIN, { emitEvent: false });
        this.updatePermissionAndAdminValidation();
        this.updatePasswordValidation();
        this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    }

    private patchHrProfile(user: UserModel): void {
        const social = this.extractSocialFields(user.social_links);
        this.form.patchValue(
            {
                user_id: user.user_id ?? null,
                name: user.name ?? null,
                phone: user.phone ?? null,
                phone_secondary: user.phone_secondary ?? null,
                photo_url: user.photo_url ?? null,
                birth_date: this.toDateTime(user.birth_date),
                marital_status: user.marital_status ?? null,
                email: user.email ?? null,
                email_secondary: user.email_secondary ?? null,
                sector: user.sector ?? null,
                manager_user_id: user.manager_user_id ?? null,
                admission_date: this.toDateTime(user.admission_date),
                termination_date: this.toDateTime(user.termination_date),
                current_role_title: user.current_role_title ?? null,
                street: user.street ?? null,
                number: user.number ?? null,
                complement: user.complement ?? null,
                neighboorhod: user.neighboorhod ?? null,
                city: user.city ?? null,
                observation: user.observation ?? null,
                cep: user.cep ?? null,
                uf: user.uf ?? null,
                type_doc: user.type_doc ?? EDocumentType.CPF,
                doc_number: user.doc_number ?? null,
                bank_name: user.bank_name ?? null,
                bank_agency: user.bank_agency ?? null,
                bank_account: user.bank_account ?? null,
                bank_account_type: user.bank_account_type ?? null,
                bank_pix: user.bank_pix ?? null,
                bank_holder: user.bank_holder ?? null,
                social_instagram: social.instagram,
                social_facebook: social.facebook,
                social_linkedin: social.linkedin,
                emergency_contact_name: user.emergency_contact_name ?? null,
                emergency_contact_phone: user.emergency_contact_phone ?? null,
                emergency_contact_relation: user.emergency_contact_relation ?? null,
                sale_comission: user.sale_comission ?? null,
                type_user: user.type_user ?? null,
                is_active: user.is_active !== false,
                permission_id: user.permission?.permission_id ?? (user as any).permission_id ?? null,
                company_id: user.company_id ?? user.company?.company_id ?? null,
            } as any,
            { emitEvent: false },
        );
        this.setDocumentsFromUser(user);
        this.syncPrimaryDocFromDocuments();
        if (user.type_doc || this.form.controls.type_doc.value) {
            this.updateDocumentValidation(this.form.controls.type_doc.value);
        }
    }

    private createDocRow(doc?: Partial<UserDocument>): DocRowForm {
        return this.fb.group({
            doc_type: [doc?.doc_type ?? null, Validators.required],
            doc_number: [doc?.doc_number ?? null, Validators.required],
            observation: [doc?.observation ?? null],
        }) as DocRowForm;
    }

    addIdentityDocument(docType: EUserDocumentType | string = EUserDocumentType.RG): void {
        this.identityDocuments.push(this.createDocRow({ doc_type: docType, doc_number: '' }));
    }

    addLaborDocument(docType: EUserDocumentType | string = EUserDocumentType.PIS): void {
        this.laborDocuments.push(this.createDocRow({ doc_type: docType, doc_number: '' }));
    }

    removeIdentityDocument(index: number): void {
        this.identityDocuments.removeAt(index);
        this.syncPrimaryDocFromDocuments();
    }

    removeLaborDocument(index: number): void {
        this.laborDocuments.removeAt(index);
    }

    onIdentityDocChange(): void {
        this.syncPrimaryDocFromDocuments();
        this.onDocInput();
    }

    private setDocumentsFromUser(user: UserModel): void {
        this.identityDocuments.clear();
        this.laborDocuments.clear();
        const docs = user.documents?.length
            ? user.documents
            : user.doc_number
              ? [{ doc_type: EUserDocumentType.CPF, doc_number: user.doc_number }]
              : [];

        for (const doc of docs) {
            const type = String(doc.doc_type || '').toUpperCase() as EUserDocumentType;
            if (USER_IDENTITY_DOCUMENT_TYPES.includes(type)) {
                this.identityDocuments.push(this.createDocRow(doc));
            } else if (USER_LABOR_DOCUMENT_TYPES.includes(type) || type === EUserDocumentType.OTHER) {
                this.laborDocuments.push(this.createDocRow(doc));
            } else if (doc.doc_number) {
                this.laborDocuments.push(this.createDocRow({ ...doc, doc_type: EUserDocumentType.OTHER }));
            }
        }

        if (this.identityDocuments.length === 0) {
            this.addIdentityDocument(EUserDocumentType.CPF);
            if (user.doc_number) {
                this.identityDocuments.at(0).patchValue({ doc_number: user.doc_number }, { emitEvent: false });
            }
        }
    }

    private collectDocumentsPayload(): UserDocument[] {
        const rows = [...this.identityDocuments.getRawValue(), ...this.laborDocuments.getRawValue()];
        return rows
            .map((d) => ({
                doc_type: String(d.doc_type || '').trim().toUpperCase(),
                doc_number: String(d.doc_number || '').trim(),
                observation: d.observation?.trim() || null,
            }))
            .filter((d) => d.doc_type && d.doc_number);
    }

    private syncPrimaryDocFromDocuments(): void {
        const docs = this.collectDocumentsPayload();
        const cpf = docs.find((d) => d.doc_type === EUserDocumentType.CPF);
        if (cpf?.doc_number) {
            this.form.controls.type_doc.setValue(EDocumentType.CPF, { emitEvent: false });
            this.form.controls.doc_number.setValue(cpf.doc_number.replace(/\D/g, ''), { emitEvent: false });
            this.updateDocumentValidation(EDocumentType.CPF);
        }
    }

    private extractSocialFields(links: { title: string; url: string }[] | null | undefined): {
        instagram: string | null;
        facebook: string | null;
        linkedin: string | null;
    } {
        const find = (name: string) =>
            (links || []).find((l) => String(l.title || '').trim().toLowerCase() === name)?.url?.trim() || null;
        return {
            instagram: find('instagram'),
            facebook: find('facebook'),
            linkedin: find('linkedin'),
        };
    }

    private buildSocialLinksPayload(raw: any): { title: string; url: string }[] | null {
        const links = [
            { title: 'Instagram', url: String(raw.social_instagram || '').trim() },
            { title: 'Facebook', url: String(raw.social_facebook || '').trim() },
            { title: 'LinkedIn', url: String(raw.social_linkedin || '').trim() },
        ].filter((l) => l.url);
        return links.length ? links : null;
    }

    onSelectPhoto(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) return;
        const file = input.files[0];
        if (!file.type.startsWith('image/')) {
            this.toast.error('Selecione apenas arquivos de imagem.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            this.toast.error('A foto deve ter no máximo 5MB.');
            return;
        }
        this.pendingPhoto = file;
        const reader = new FileReader();
        reader.onload = (e) => {
            this.photoPreview = (e.target?.result as string) || null;
        };
        reader.readAsDataURL(file);
        this.form.controls.photo_url.setValue('pending_upload');
        this.toast.info('Foto selecionada. Clique em Salvar para enviar.');
        input.value = '';
    }

    removePhoto(): void {
        this.pendingPhoto = null;
        this.photoPreview = null;
        this.form.controls.photo_url.setValue(null);
    }

    private toDateTime(v: Date | string | DateTime | null | undefined): DateTime | null {
        if (!v) return null;
        if (DateTime.isDateTime(v)) return v.isValid ? v : null;
        if (v instanceof Date) return DateTime.fromJSDate(v);
        if (typeof v === 'string') {
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
                const dt = DateTime.fromFormat(v, 'dd/MM/yyyy');
                return dt.isValid ? dt : null;
            }
            const iso = DateTime.fromISO(String(v).slice(0, 10));
            return iso.isValid ? iso : null;
        }
        return null;
    }

    private toIsoDate(v: DateTime | Date | string | null | undefined): string | null {
        if (!v) return null;
        if (typeof v === 'string') {
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(v.trim())) {
                const dt = DateTime.fromFormat(v.trim(), 'dd/MM/yyyy');
                return dt.isValid ? dt.toISODate() : null;
            }
            if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
            return null;
        }
        if (DateTime.isDateTime(v)) return v.isValid ? v.toISODate() : null;
        if (v instanceof Date) return DateTime.fromJSDate(v).toISODate();
        return null;
    }
}
